import { useTranslation } from 'react-i18next';

import { AccountProfileElement } from '@shared/dream-components';

import { useAccountProfileContext } from '~/context/SubscriptionManagement';

import { PendingGroupInvites } from './PendingGroupInvites';

/**
 * Renders the account-profile card (avatar / name / email + the edit / connections /
 * reset-password / logout dropdown). Every input — profile fields, openers, and the
 * social-login providers — comes from `AccountProfileProvider` via context. Pure UI, no props.
 */
export const AccountProfileSection = () => {
  const { t, i18n } = useTranslation();
  const {
    email,
    firstName,
    lastName,
    avatarUrl,
    hasEmailChangePending,
    createdAt,
    premiumSince,
    showProfileActions,
    canResetPassword,
    socialLoginProviders,
    isProfileLoading,
    pendingGroupInvites,
    onClickEditProfile,
    onClickConnections,
    onClickResetPassword,
    onClickLogout,
  } = useAccountProfileContext();

  return (
    <>
      <PendingGroupInvites invites={pendingGroupInvites} />

      <AccountProfileElement
        element={{
          type: 'accountProfile',
          attrs: {
            data: {
              email,
              firstName,
              lastName,
              avatarUrl,
              emailChangeStatusBadge: hasEmailChangePending
                ? {
                    type: 'warning',
                    text: t('manage.account_profile.form.email_change_pending_badge_label'),
                  }
                : undefined,
            },
          },
        }}
        showActions={showProfileActions}
        translations={{
          'manage.account_profile.title': t('manage.account_profile.title'),
          'manage.account_profile.description': t('manage.account_profile.description'),
          'manage.account_profile.form.email_label': t('manage.account_profile.form.email_label'),
          'manage.account_profile.form.name_label': t('manage.account_profile.form.name_label'),
          'manage.account_profile.dropdown_options.edit_profile_label': t(
            'manage.account_profile.dropdown_options.edit_profile_label',
          ),
          'manage.account_profile.dropdown_options.connections_label': t(
            'manage.account_profile.dropdown_options.connections_label',
          ),
          'manage.account_profile.dropdown_options.reset_password_label': t(
            'manage.account_profile.dropdown_options.reset_password_label',
          ),
          'manage.account_profile.dropdown_options.logout': t('manage.account_profile.dropdown_options.logout'),
          'manage.update_profile_modal.form.email_change_helper_text': t(
            'manage.update_profile_modal.form.email_change_helper_text',
          ),
        }}
        isLive
        canResetPassword={canResetPassword}
        socialLoginProviders={socialLoginProviders}
        onClickEditProfile={onClickEditProfile}
        onClickConnections={onClickConnections}
        onClickResetPassword={onClickResetPassword}
        onClickLogout={onClickLogout}
        isLoading={isProfileLoading}
      >
        <div className="space-y-1">
          <p className="text-xs text-gray-400">
            {t('manage.subscriber_since.subscriber', {
              date: new Date(createdAt).toLocaleDateString(i18n.language, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
            })}
          </p>
          {premiumSince && (
            <p className="text-xs text-gray-400">
              {t('manage.subscriber_since.premium', {
                date: new Date(premiumSince).toLocaleDateString(i18n.language, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              })}
            </p>
          )}
        </div>
      </AccountProfileElement>
    </>
  );
};
